// Basic
import PropTypes from 'prop-types';
// Components
import Entity from '@/common-utils/components/renders/entity/Entity';
import Icon from '@/common-utils/components/renders/icon/Icon';
// Common
import ICONS from '@/common-utils/enums/icons';
import contractFields from '@/app/modules/contracts/fields/contractFields';
import APPLICATIONS, { findApplicationFromCode } from '@/app/modules/contracts/enums/ItpRegex';
// Lang
import { nsBeneficiaries, nsTerminal } from '@/app/i18n/namespaces';
import TooltipHelper from '@/common-utils/components/renders/tooltip/TooltipHelper';

const getAppLogo = (applicationCode) => {
    const found = applicationCode && findApplicationFromCode(applicationCode);
    return found?.logo ?? APPLICATIONS.CBEMV.logo;
};

/**
 * @param root0
 * @param root0.contract
 * @param root0.submitDissociateContract
 * @param root0.showActions
 * @param root0.displayVariant
 * @returns {JSX} ContractCard
 */
function ContractCard({
    contract,
    submitDissociateContract,
    showActions = true,
    displayVariant = 'default'
}) {
    return (
        <div className='w-[300px] border border-secondary-hover rounded-md bg-muted/30 overflow-hidden shrink-0'>
            {/* Card header */}
            <div className='flex items-center gap-2 px-3 py-2 border-b bg-secondary-hover'>
                <Entity
                    className='text-sm text-dark-foreground'
                    entity={contract[contractFields.NUMBER.altKey]}
                />
                <Entity
                    className='text-sm text-labelGray-foreground'
                    entity={contract[contractFields.RANK.key]}
                />
                <Entity
                    className='text-sm text-labelGray-foreground'
                    entity={contract[contractFields.BANK_CODE.key]}
                />
                {
                    displayVariant === 'default' &&
                    <img
                        src={getAppLogo(contract[contractFields.APPLICATION.key])}
                        alt='logo'
                        className='h-6 w-auto ml-auto'
                    />
                }
                {showActions && (
                    <Icon
                        icon={ICONS.LINK_OFF}
                        className='w-4 h-4 cursor-pointer text-dark-foreground hover:text-danger shrink-0'
                        onClick={submitDissociateContract}
                    />
                )}
            </div>
            {/* Card body */}
            <div className='flex flex-col gap-2 px-3 py-4'>
                {displayVariant === 'tpe' ? (
                    <>
                        <div className='flex items-center justify-between gap-2'>
                            <Entity
                                className='text-sm text-labelGray-foreground'
                                entity='payludd.monetic_contract.uncaptured_transactions'
                                namespace={nsTerminal}
                            />
                            <Entity
                                className='text-sm font-semibold'
                                entity={contract.hasTransaction === true
                                    ? 'payludd.monetic_contract.yes'
                                    : 'payludd.monetic_contract.no'}
                                namespace={nsTerminal}
                            />
                        </div>
                        <div className='flex items-center justify-between gap-2'>
                            <Entity
                                className='text-sm text-labelGray-foreground'
                                entity='payludd.monetic_contract.tlp_ready'
                                namespace={nsTerminal}
                            />
                            <Entity
                                className='text-sm font-semibold'
                                entity={contract.isReady === true
                                    ? 'payludd.monetic_contract.yes'
                                    : 'payludd.monetic_contract.no'}
                                namespace={nsTerminal}
                            />
                        </div>
                    </>
                ) : (
                    <>
                        <div className='flex items-center gap-2'>
                            <TooltipHelper
                                trigger={<Icon icon={ICONS.POS_TERMINAL_REMITTANCE} />}
                                content={<Entity entity='actions.contract.merchant_name' namespace={nsBeneficiaries} />}
                            />
                            <Entity
                                className='text-sm font-semibold uppercase'
                                entity={contract[contractFields.MERCHANT_NAME.key]}
                            />
                        </div>
                        <div className='flex items-center gap-2'>
                            <TooltipHelper
                                trigger={<Icon icon={ICONS.GAVEL} />}
                                content={<Entity entity='actions.contract.legal_name' namespace={nsBeneficiaries} />}
                            />
                            {contract[contractFields.MERCHANT_LEGAL_NAME.key] ? (
                                <Entity
                                    className='text-sm font-semibold uppercase'
                                    entity={contract[contractFields.MERCHANT_LEGAL_NAME.key]}
                                />
                            ) : (
                                <Entity
                                    className='text-sm text-warning italic'
                                    entity='actions.beneficiary.no_legal_info' namespace={nsBeneficiaries}
                                />
                            )}
                        </div>
                    </>
                )}
            </div>
        </div >
    );
}

ContractCard.propTypes = {
    contract: PropTypes.object.isRequired,
    submitDissociateContract: PropTypes.func.isRequired,
    showActions: PropTypes.bool,
    displayVariant: PropTypes.oneOf(['default', 'tpe'])
};

export default ContractCard;

